import { AxiosInstance } from 'axios';
import { BaseRequest } from '@trukt/types/BaseRequest';
import { BaseRequestEndpoints } from '@trukt/types/BaseRequestEndpoints';
import { PaginatedQueryParams } from '@trukt/types/PaginatedQueryParams';
import { PaginatedResults } from '@trukt/types/PaginatedResults';
import { Contact } from '@trukt/types/Contact';
import { NonNullableKeys } from '@trukt/types/NonNullableKeys';

interface AccountContactSearchRequestQueryParams extends PaginatedQueryParams {
  isDeleted?: boolean;
}

type BaseRequestRouteParams = {
  accountGuid: string;
};

interface BaseRequestBody
  extends Required<Pick<Contact, 'name'>>,
    Partial<NonNullableKeys<Pick<Contact, 'email' | 'phoneNumber' | 'mobileNumber'>>> {}

export interface AccountContactSearchRequestRouteParams extends BaseRequestRouteParams {}

export type AccountContactSearchRequest = BaseRequest<
  AccountContactSearchRequestQueryParams,
  void,
  AccountContactSearchRequestRouteParams
>;

export type AccountContactSearchResponse = PaginatedResults<
  Pick<Contact, 'guid' | 'name' | 'email' | 'phoneNumber' | 'mobileNumber'>
>;

export interface AccountContactCreateRequestRouteParams extends BaseRequestRouteParams {}
export interface AccountContactCreateRequestBody extends BaseRequestBody {}

export type AccountContactCreateRequest = BaseRequest<
  void,
  AccountContactCreateRequestBody,
  AccountContactCreateRequestRouteParams
>;

export type AccountContactCreateResponse = {
  guid: string;
};

export interface AccountContactUpdateRequestRouteParams extends BaseRequestRouteParams {
  contactGuid: string;
}

export interface AccountContactUpdateRequestBody extends BaseRequestBody {}

export type AccountContactUpdateResponse = void;
export type AccountContactUpdateRequest = BaseRequest<
  void,
  AccountContactUpdateRequestBody,
  AccountContactUpdateRequestRouteParams
>;

export type AccountContactDeleteResponse = void;
export type AccountContactDeleteRequest = BaseRequest<
  void,
  void,
  AccountContactUpdateRequestRouteParams
>;

export class AccountContactService implements Pick<BaseRequestEndpoints, 'search' | 'create'> {
  instance: AxiosInstance;
  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  search(request: AccountContactSearchRequest) {
    return this.instance.get<AccountContactSearchResponse>(
      `/account/${request.routeParams.accountGuid}/contacts/search`,
      {
        params: request.queryParams,
      },
    );
  }

  create(request: AccountContactCreateRequest) {
    return this.instance.post<AccountContactCreateResponse>(
      `/account/${request.routeParams.accountGuid}/contacts`,
      request.body,
    );
  }

  update(request: AccountContactUpdateRequest) {
    return this.instance.put<AccountContactUpdateResponse>(
      `/account/${request.routeParams.accountGuid}/contacts/${request.routeParams.contactGuid}`,
      request.body,
    );
  }

  delete(request: AccountContactDeleteRequest) {
    return this.instance.delete<AccountContactDeleteResponse>(
      `/account/${request.routeParams.accountGuid}/contacts/${request.routeParams.contactGuid}`,
    );
  }
}
